/** GUIDED NEXT ACTION — one uniform card for orders, devices, tickets, recoveries, deployments. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, User, CalendarClock, Gauge, Activity, CheckCircle2, ArrowRight, Loader2, Hourglass } from "lucide-react";

const SLA_TONE = { on_track: "bg-emerald-50 text-emerald-700 border-emerald-200", at_risk: "bg-amber-50 text-amber-700 border-amber-200", breached: "bg-rose-50 text-rose-700 border-rose-200", none: "bg-slate-100 text-slate-500 border-slate-200" };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function Fact({ icon: Icon, label, value, testid, children }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[color:var(--st-text-subtle)] inline-flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</div>
      <div className="text-[13px] font-semibold text-[color:var(--st-text)] truncate mt-0.5">{children || value || "—"}</div>
    </div>
  );
}

export function StepChain({ steps }) {
  if (!steps?.length) return null;
  return (
    <ol className="flex flex-wrap items-center gap-1 text-[11px]" data-testid="next-action-steps">
      {steps.map((s, i) => (
        <li key={s.key} className="inline-flex items-center gap-1">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border ${s.state === "done" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : s.state === "active" ? "bg-[color:var(--st-text)] border-transparent text-white font-semibold" : "bg-white border-[color:var(--st-border)] text-[color:var(--st-text-muted)]"}`} data-testid={`step-${s.key}-${s.state}`}>
            {s.state === "done" && <CheckCircle2 className="h-3 w-3" />}{s.label}
          </span>
          {i < steps.length - 1 && <ArrowRight className="h-3 w-3 text-[color:var(--st-text-subtle)]" />}
        </li>
      ))}
    </ol>
  );
}

export default function NextActionPanel({ kind, id, onChanged, compact = false }) {
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/next-action/${kind}/${encodeURIComponent(id)}`).then(({ data }) => setCard(data)).catch(() => setCard(null)), [kind, id]);
  useEffect(() => { if (id) load(); }, [load, id]);
  if (!card) return <div className="os-skeleton h-28" data-testid="next-action-loading" />;

  const p = card.primary;
  const run = async () => {
    if (!p) return;
    if (p.type === "navigate") { navigate(p.route); return; }
    setBusy(true);
    try {
      await api.request({ url: p.endpoint, method: p.method || "POST", data: p.payload || {} });
      toast.success(`${p.label} — done`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); if (p.route) navigate(p.route); }
    setBusy(false);
  };
  const sla = card.sla || {};
  return (
    <section className={`os-card relative overflow-hidden ${card.done ? "" : "border-[color:var(--st-accent)]/40"} ${compact ? "p-3" : "p-4 sm:p-5"}`} data-testid={`next-action-panel-${kind}`}>
      {!card.done && <span className="absolute left-0 top-0 bottom-0 w-1 bg-[color:var(--st-accent)]" />}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 pl-1">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[color:var(--st-text-subtle)]" data-testid="next-action-stage">{card.stage}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold flex items-center gap-1.5 text-[color:var(--st-accent)] mt-1"><Sparkles className="h-3.5 w-3.5" /> Next action{card.next_action?.waiting_on ? ` · waiting on ${card.next_action.waiting_on.toLowerCase()}` : ""}</div>
          <h2 className={`font-display font-bold tracking-tight text-[color:var(--st-text)] ${compact ? "text-base" : "text-lg sm:text-2xl"}`} data-testid="next-action-label">{card.next_action?.label}</h2>
          {card.next_action?.why && <p className="text-[12px] text-[color:var(--st-text-muted)] mt-0.5">{card.next_action.why}</p>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            <Fact icon={User} label="Owner" value={card.owner} testid="next-action-owner" />
            <Fact icon={CalendarClock} label="Due date" value={fmtDate(card.due_date)} testid="next-action-due" />
            <Fact icon={Gauge} label="SLA" testid="next-action-sla"><span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] ${SLA_TONE[sla.state] || SLA_TONE.none}`}>{sla.label}</span></Fact>
            <Fact icon={Activity} label="Status" testid="next-action-status"><span className="font-mono text-[12px]">{String(card.status || "—").replace(/_/g, " ")}</span></Fact>
          </div>
        </div>
        {p ? (
          <button onClick={run} disabled={busy} className="h-12 px-6 rounded-full bg-[color:var(--st-accent)] text-white text-[13px] font-bold tracking-wide inline-flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-60 whitespace-nowrap shadow-sm" data-testid="next-action-primary">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} [ {p.label} ]
          </button>
        ) : card.done ? <span className="inline-flex items-center gap-1.5 text-emerald-700 text-[13px] font-semibold" data-testid="next-action-done"><CheckCircle2 className="h-4 w-4" /> Nothing pending</span>
          : <span className="inline-flex items-center gap-1.5 text-[color:var(--st-text-muted)] text-[13px]" data-testid="next-action-waiting"><Hourglass className="h-4 w-4" /> Waiting</span>}
      </div>
      {!compact && <div className="mt-4 pl-1"><StepChain steps={card.steps} /></div>}
    </section>
  );
}
